import React, { FC } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { useActiveClientContext } from 'store/active-client';
import { useWizardContext } from 'store/wizard';
import { buildClientUrl } from 'utils/helpers/client';
import TooltipWrapper from 'components/TooltipWrapper';
import Button from 'components/Button';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import FormButtons from 'components/form/FormButtons';
import { useTranslation } from 'react-i18next';

export interface IProps {
  currentStep: number;
  isLastStep?: boolean;
  isSubmitting: boolean;
  isSubmitDisabled?: boolean;
  backStep: number;
  backStepInvisible?: any;
  submitLabel?: string;
  ariaLabel?: string;
}

const WizardNavAndSubmit: FC<IProps> = ({
  currentStep,
  isLastStep,
  isSubmitting,
  isSubmitDisabled,
  backStep,
  backStepInvisible,
  submitLabel,
  ariaLabel,
}: IProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { setCurrentStep } = useWizardContext();
  const isFirstStep = currentStep === 1;
  const params = useParams<{ id: string | undefined }>();

  // Flag to recognize doc mode - edit or create
  const holderId = params.id || '';

  return (
    <NavButtonsHolder aria-label={ariaLabel ?? 'Wizard Navigation'}>
      <TooltipWrapper
        id="tooltip-back"
        overlay={t('docWizard.prevStep')}
        show={isFirstStep || backStepInvisible ? false : undefined}
      >
        <Button
          size="sm"
          btnType="secondary"
          outlined
          className={isFirstStep || backStepInvisible ? 'invisible' : ''}
          onClick={() => {
            setCurrentStep(backStep);
            const url = buildClientUrl(`/documents-wizard/step-${backStep}/${holderId}`, client);
            history.push({
              pathname: url,
              state: {
                ...(backStep === 1 ? { isNew: 0 } : {}),
              },
            });
          }}
          disabled={isSubmitDisabled || isSubmitting}
        >
          <BiChevronLeft size={20} />
        </Button>
      </TooltipWrapper>
      <div className="ml-3 mr-3">
        <FormButtons
          btnLabel={submitLabel || t('common.saveAndContinue')}
          hideCancelBtn
          isHorizontal
          areBtnsCentered
          isSubmitting={isSubmitting}
          isSubmitDisabled={isSubmitDisabled}
        />
      </div>
      <TooltipWrapper
        id="tooltip-forth"
        overlay={t('docWizard.nextStep')}
        show={isLastStep ? false : undefined}
      >
        <Button
          size="sm"
          btnType="secondary"
          outlined
          className={isLastStep ? 'invisible' : ''}
          disabled={isSubmitDisabled || isSubmitting}
          loading={isSubmitting}
          type="submit"
        >
          <BiChevronRight size={20} />
        </Button>
      </TooltipWrapper>
    </NavButtonsHolder>
  );
};

const NavButtonsHolder = styled.div`
  margin-top: 1rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

export default WizardNavAndSubmit;
